/**
 * Which cancelled order lines need a person's decision, and what they may do.
 * Pure: no Vue, no aws-amplify. See the 2026-09-26 cancellations spec, s3/s5,
 * and its BINDING AMENDMENT §A6.
 */

// While RELEASE_CANCELLED_ENABLED is "false" in
// amplify/backend/function/taskCheckSoldItems/taskCheckSoldItems-cloudformation-template.json
// this MUST stay the sentinel below -- never the template's real
// RELEASE_CANCELLED_CUTOFF_AT -- so the Cancellations page cannot surface
// anything before the release is actually turned on (Task 11). Once it flips
// to "true", this constant must equal the template's cutoff exactly; both
// move together in that same commit.
// See tools/oms/__tests__/cancellation-cutoff-matches-template.test.js.
export const CANCELLATION_CUTOFF_AT = "2026-09-29T02:10:23.000Z";

// The "Sold on" choices when a cancelled line was sold elsewhere. Lowercase:
// removeOtherLiveListings compares against the lowercased account type.
export const SOLD_ELSEWHERE_CHANNELS = ['ebay', 'poshmark', 'medusa', 'shopify', 'depop', 'consigncloud', 'other'];

export const OUTCOMES = {
  RELISTED: "relisted",
  MISSING: "missing",
  SOLD_ELSEWHERE: "sold_elsewhere",
  DISMISSED: "dismissed",
};

/** Outcome -> the Inventory.status it stamps. Per spec §10.1. */
export const OUTCOME_TARGET = { relisted: "Published", missing: "Missing", sold_elsewhere: "Sold" };

/**
 * Readable text for a failed call. aws-amplify v4 rejects a failed
 * API.graphql with the result object `{ data, errors }`, not an Error, so
 * `e.message` is undefined and String(e) is "[object Object]" (OPT-71).
 */
export function describeError(e) {
  if (e && Array.isArray(e.errors) && e.errors.length) {
    const text = e.errors.map((x) => (x && x.message) || String(x)).join("; ");
    if (text) return text;
  }
  if (e && e.message) return e.message;
  return typeof e === "string" && e ? e : "Something went wrong.";
}

/** Epoch ms, or null. Never compare these timestamps as strings. */
export function parseInstant(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

// Equal to RETURN_RE in tools/oms/poshmark-return.cjs (parity test); src/ does
// not import from tools/.
const POSHMARK_RETURN_RE = /\breturn\b/i;

/** Poshmark's final display_status when it names a return, else null. */
export function poshmarkReturnStatus(order) {
  if (!order || order.channel !== "poshmark") return null;
  const s = typeof order.channelNativeStatus === "string" ? order.channelNativeStatus.trim() : "";
  return s && POSHMARK_RETURN_RE.test(s) ? s : null;
}

/**
 * `cancelledAtOverride` (optional): the cancellation instant the release
 * itself compares -- the earliest `cancelled` OrderStatusEvent's observedAt,
 * which the loader reads. Without it, cancelledAt falls back to the order's
 * sourceUpdatedAt (eBay lastModifiedDate, Medusa updated_at), which can move
 * for reasons other than the cancel.
 */
export function cancellationRowFor(order, line, match, inventory, decision, cancelledAtOverride) {
  const matchState = (match && match.state) || "unmatched";
  // The item is shown when the line is matched to it, OR when a decision was
  // made about it: Find item's choice is recorded on the decision, so a row
  // decided before the match write existed still shows its item.
  const hasItem = !!inventory && (
    matchState === "matched" || !!(decision && decision.inventoryId === inventory.id)
  );
  const listings = (hasItem && inventory.listings && inventory.listings.items) || [];
  const sellingListing = listings.find(
    (l) => l && l.marketplaceID === order.channelAccountRef && l.status === "cancelled"
  ) || null;
  return {
    lineId: line.id,
    orderId: order.id,
    orderStatus: order.status,
    ownerId: order.owner,
    externalOrderId: order.externalOrderId,
    channel: order.channel,
    returnStatus: poshmarkReturnStatus(order),
    channelAccountRef: order.channelAccountRef,
    cancelledAt: cancelledAtOverride || order.sourceUpdatedAt || null,
    // A line that arrived without a SKU shows its item's: never blank when
    // the item is known.
    sku: line.sku || (hasItem && inventory.sku) || (decision && decision.sku) || null,
    title: line.titleSnapshot || null,
    salePriceCents: line.salePriceCents == null ? null : line.salePriceCents,
    matchState,
    inventoryId: hasItem ? inventory.id : null,
    inventory: hasItem ? inventory : null,
    // A staff-chosen (manual) match: the item was never parked by the
    // release, so availableActions treats its Sold as the cancelled sale's.
    attached: matchState === "matched" && !!match && match.method === "manual",
    sellingListing,
    decision: decision || null,
  };
}

/** Strictly after the cutoff -- a cancel exactly at it does not surface. */
export function needsDecision(row, cutoffMs) {
  if (!row || row.orderStatus !== "cancelled" || row.decision) return false;
  const at = parseInstant(row.cancelledAt);
  return at !== null && at > cutoffMs;
}

/** The outcome a part-way resolution already applied to the item, or null. */
export function resumableOutcome(row) {
  if (!row.inventory || row.decision) return null;
  const resolved = parseInstant(row.inventory.cancelResolvedAt);
  const cancelled = parseInstant(row.cancelledAt);
  if (resolved === null || cancelled === null || resolved < cancelled) return null;
  const hit = Object.entries(OUTCOME_TARGET).find(([, status]) => status === row.inventory.status);
  return hit ? hit[0] : null;
}

export function availableActions(row) {
  if (!row.inventory) return ["find", "dismiss"];
  const resume = resumableOutcome(row);
  if (resume) return ["finish:" + resume];
  const status = row.inventory.status;
  const full = ["relist", "missing", "sold_elsewhere"];
  if (status === "Cancelled") return full;
  // An item attached through Find item was never parked by the release,
  // so its Sold is the cancelled sale's own Sold.
  if (row.attached && typeof status === "string" && status.toLowerCase() === "sold") return full;
  return ["sold_elsewhere", "dismiss"];
}

/** Attach a staff-found item to an unmatched row, once the match is saved
 * (api/cancellationMatch.js). The row then reads as a matched one. */
export function attachInventory(row, inventory) {
  return {
    ...row, inventory, inventoryId: inventory.id, matchState: "matched",
    sku: row.sku || inventory.sku || null, attached: true, sellingListing: null,
  };
}
